import React, { useState, useEffect, useRef, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/homeapi";
import QRCodeLib from "qrcode";
import { downloadTicketsPdf } from "../utils/ticketPdf";
import { useAuth } from "../context/AuthContext";
import { useShowToast, useNotify, errorMessage } from "../components/Notify";
import { HomeIcon, LogoutIcon } from "../components/Icons";

/* Petit QR code canvas - utilisé pour l'aperçu du billet confirmé */
const TicketQR = ({ data, size = 90 }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data) return;
    QRCodeLib.toCanvas(ref.current, data, { width: size, color: { dark: "#1a3d2b", light: "#ffffff" }, errorCorrectionLevel: "H" }).catch(() => {});
  }, [data, size]);
  return <canvas ref={ref} width={size} height={size} style={{ borderRadius: 8, border: "2px solid #1a3d2b" }} />;
};

/* ─────────────────────────── TOKENS ─────────────────────────── */
const T = {
  forest:     "#1a3d2b",
  forestMid:  "#2d5a3f",
  gold:       "#c8942a",
  goldLight:  "#e8b84b",
  cream:      "#f5f0e8",
  creamDark:  "#ede6d6",
  rust:       "#8b3a1a",
  ink:        "#0e1f17",
  smoke:      "#8a9e93",
  white:      "#fafaf7",
};

/* ─────────────────────────── HELPERS ─────────────────────────── */
const STATUS = {
  CONFIRMED: { bg: "#d1fae5", color: "#065f46", dot: "#22a76f", label: "Confirmé" },
  PENDING:   { bg: "#fef3c7", color: "#92400e", dot: "#c8942a", label: "En attente" },
  CANCELLED: { bg: "#fee2e2", color: "#991b1b", dot: "#8b3a1a", label: "Annulé" },
};

const CLASS_MAP = {
  ECONOMIQUE: { icon: "🚃", label: "Économique" },
  PREMIERE:   { icon: "⭐", label: "1ère Classe" },
  VIP:        { icon: "👑", label: "VIP" },
  DEUXIEME:   { icon: "🚋", label: "2ème Classe" },
};

const fmtPrice = (n) => Number(n || 0).toLocaleString("fr-FR") + " FCFA";

const STATIONS = [
  "Libreville", "Owendo", "Ntoum", "Oyan", "Abanga",
  "Bifoun", "Lambaréné", "Makouké", "Booué", "Lastoursville",
  "Moanda", "Franceville", "Lopé", "Ayem", "Ivindo",
];

/* ─────────────────────────── STYLES ─────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }

body {
  font-family: 'DM Sans', sans-serif;
  background: ${T.cream};
  color: ${T.ink};
  min-height: 100vh;
}

body::before {
  content: '';
  position: fixed; inset: 0;
  background-image:
    radial-gradient(circle at 20% 20%, rgba(26,61,43,.06) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(200,148,42,.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.setrag-dash { position: relative; z-index: 1; }

.nav {
  background: ${T.forest};
  padding: 0 clamp(16px, 4vw, 48px);
  position: sticky; top: 0; z-index: 100;
  box-shadow: 0 4px 32px rgba(14,31,23,.18);
}
.nav-inner {
  max-width: 1100px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
  height: 64px; gap: 12px;
}
.nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
.nav-logo-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: ${T.gold};
  display: flex; align-items: center; justify-content: center; font-size: 18px;
}
.nav-logo-title { font-family: 'Playfair Display', serif; font-weight: 900; font-size: 19px; color: ${T.white}; letter-spacing: .04em; }
.nav-logo-sub { font-size: 9px; color: ${T.smoke}; letter-spacing: .12em; text-transform: uppercase; }

.nav-actions { display: flex; align-items: center; gap: 10px; }
.nav-btn-ghost {
  background: transparent; border: none; color: rgba(255,255,255,.7);
  font-family: 'DM Sans', sans-serif; font-size: 13px; cursor: pointer;
  padding: 7px 12px; border-radius: 8px; transition: all .2s;
}
.nav-btn-ghost:hover { background: rgba(255,255,255,.08); color: ${T.white}; }

.nav-btn-outline {
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.25);
  color: ${T.white}; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
  cursor: pointer; padding: 7px 16px; border-radius: 8px; transition: all .2s;
}
.nav-btn-outline:hover { background: rgba(255,255,255,.18); }

.avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(135deg, ${T.gold}, ${T.goldLight});
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 13px; color: ${T.forest}; flex-shrink: 0;
}

.dash-main { max-width: 1100px; margin: 0 auto; padding: clamp(20px, 4vw, 40px) clamp(16px, 4vw, 48px); }

.profile-card {
  background: ${T.white};
  border-radius: 20px;
  border: 1.5px solid ${T.creamDark};
  padding: clamp(20px, 3vw, 28px);
  margin-bottom: 24px;
  display: flex; align-items: center; gap: 20px;
  flex-wrap: wrap;
  animation: fadeUp .5s ease both;
}
.profile-avatar {
  width: 64px; height: 64px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, ${T.gold}, ${T.goldLight});
  display: flex; align-items: center; justify-content: center;
  font-family: 'Playfair Display', serif; font-weight: 700; font-size: 22px; color: ${T.forest};
}
.profile-name { font-family: 'Playfair Display', serif; font-size: clamp(18px, 3vw, 22px); font-weight: 700; }
.profile-meta { font-size: 13px; color: ${T.smoke}; margin-top: 4px; }
.profile-stats { margin-left: auto; display: flex; gap: 20px; flex-wrap: wrap; }
.profile-stat { text-align: center; }
.profile-stat-val { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 700; color: ${T.forest}; line-height: 1; }
.profile-stat-label { font-size: 11px; color: ${T.smoke}; text-transform: uppercase; letter-spacing: .08em; margin-top: 2px; }

.stats-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 14px; margin-bottom: 28px;
}
.stat-card {
  background: ${T.white}; border-radius: 16px;
  border: 1.5px solid ${T.creamDark};
  padding: 18px 16px;
  display: flex; align-items: center; gap: 12px;
  transition: transform .2s, box-shadow .2s;
  animation: fadeUp .5s ease both;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(14,31,23,.1); }
.stat-icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.stat-icon.green { background: rgba(26,61,43,.1); }
.stat-icon.gold  { background: rgba(200,148,42,.1); }
.stat-icon.rust  { background: rgba(139,58,26,.1); }
.stat-val { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 700; line-height: 1; }
.stat-label { font-size: 11px; color: ${T.smoke}; font-weight: 500; margin-top: 2px; }

.autocomplete-wrap { position: relative; }
.autocomplete-list {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  background: ${T.white}; border: 1.5px solid ${T.creamDark};
  border-radius: 10px; z-index: 200;
  box-shadow: 0 8px 28px rgba(14,31,23,.12);
  max-height: 200px; overflow-y: auto;
  animation: fadeUp .15s ease both;
}
.autocomplete-item {
  padding: 9px 14px; font-size: 14px; cursor: pointer;
  transition: background .12s;
  display: flex; align-items: center; gap: 8px;
}
.autocomplete-item:hover, .autocomplete-item.active {
  background: rgba(26,61,43,.06); color: ${T.forest};
}
.autocomplete-item:first-child { border-radius: 10px 10px 0 0; }
.autocomplete-item:last-child  { border-radius: 0 0 10px 10px; }

.section { margin-bottom: 36px; animation: fadeUp .5s ease both; }
.section-title { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; margin-bottom: 16px; }

.search-card {
  background: ${T.white}; border-radius: 16px;
  border: 1.5px solid ${T.creamDark}; padding: 20px;
}
.search-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 12px; align-items: end;
}
@media (max-width: 680px) {
  .search-grid { grid-template-columns: 1fr 1fr; }
  .search-grid .search-btn-cell { grid-column: 1 / -1; }
  .search-grid .search-btn-cell label { display: none; }
}
@media (max-width: 420px) {
  .search-grid { grid-template-columns: 1fr; }
}

.field { display: flex; flex-direction: column; gap: 6px; }
.field label { font-size: 11px; font-weight: 600; color: ${T.smoke}; text-transform: uppercase; letter-spacing: .1em; }
.field input, .field select {
  width: 100%; padding: 11px 14px;
  border: 1.5px solid ${T.creamDark}; border-radius: 10px;
  font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500;
  color: ${T.ink}; background: ${T.cream}; outline: none;
  transition: border-color .2s, background .2s;
  appearance: none;
}
.field input:focus, .field select:focus {
  border-color: ${T.forest}; background: ${T.white};
  box-shadow: 0 0 0 3px rgba(26,61,43,.07);
}

.search-btn {
  width: 100%; padding: 12px 20px;
  background: ${T.forest}; color: ${T.white};
  border: none; border-radius: 10px;
  font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all .2s; white-space: nowrap;
  box-shadow: 0 4px 14px rgba(26,61,43,.28);
}
.search-btn:hover { background: #142e20; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(26,61,43,.32); }
.search-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }

.trips-list { display: flex; flex-direction: column; gap: 14px; }

.trip-card {
  background: ${T.white}; border-radius: 16px;
  border: 1.5px solid ${T.creamDark};
  padding: clamp(14px, 2.5vw, 20px);
  display: grid;
  grid-template-columns: 1fr auto 1fr 1fr auto;
  gap: 14px; align-items: center;
  transition: all .25s; cursor: pointer;
  animation: fadeUp .4s ease both;
}
.trip-card:hover { border-color: ${T.forest}; transform: translateY(-2px); box-shadow: 0 10px 36px rgba(14,31,23,.1); }

@media (max-width: 700px) {
  .trip-card { grid-template-columns: 1fr auto 1fr; grid-template-areas: "dep mid arr" "badges badges badges" "price price price"; gap: 12px 10px; }
  .trip-card .tc-dep { grid-area: dep; }
  .trip-card .trip-middle { grid-area: mid; }
  .trip-card .tc-arr { grid-area: arr; text-align: right; }
  .trip-card .trip-badges { grid-area: badges; }
  .trip-card .trip-price-cell { grid-area: price; text-align: left; }
  .trip-line { width: 56px; }
}

.station-time { font-family: 'DM Mono', monospace; font-size: clamp(18px, 3vw, 24px); font-weight: 500; line-height: 1; }
.station-name { font-size: 13px; font-weight: 600; color: ${T.forest}; margin-top: 2px; }
.station-code { font-size: 10px; color: ${T.smoke}; font-family: 'DM Mono', monospace; letter-spacing: .08em; }

.trip-middle { display: flex; flex-direction: column; align-items: center; gap: 5px; }
.trip-line { width: 80px; display: flex; align-items: center; gap: 3px; }
.trip-dot { width: 7px; height: 7px; border-radius: 50%; background: ${T.forest}; flex-shrink: 0; }
.trip-dash { flex: 1; height: 2px; background: ${T.creamDark}; position: relative; }
.trip-dash::after { content: '🚂'; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-size: 13px; }
.trip-duration { font-size: 11px; color: ${T.smoke}; background: ${T.creamDark}; padding: 2px 9px; border-radius: 50px; font-weight: 500; }

.badge { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 50px; letter-spacing: .05em; display: inline-block; margin: 2px; }
.badge-class { background: rgba(26,61,43,.1); color: ${T.forest}; }
.badge-seats { background: rgba(200,148,42,.12); color: #a07320; }
.badge-seats.low { background: rgba(139,58,26,.1); color: ${T.rust}; }
.badge-train { background: ${T.creamDark}; color: ${T.smoke}; }

.trip-price { font-family: 'Playfair Display', serif; font-size: clamp(18px, 3vw, 24px); font-weight: 700; color: ${T.forest}; }
.trip-currency { font-size: 12px; color: ${T.smoke}; margin-bottom: 6px; }

.book-btn {
  display: block; padding: 8px 16px;
  background: ${T.forest}; color: ${T.white};
  border: none; border-radius: 8px; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
  transition: all .2s; white-space: nowrap;
}
.book-btn:hover { background: ${T.gold}; transform: scale(1.03); }

.bookings-list { display: flex; flex-direction: column; gap: 12px; }
.booking-card {
  background: ${T.white}; border-radius: 14px;
  border: 1.5px solid ${T.creamDark};
  padding: clamp(14px, 2vw, 18px) clamp(16px, 2.5vw, 22px);
  display: flex; align-items: center; gap: 14px;
  transition: box-shadow .2s;
  animation: fadeUp .5s ease both;
  flex-wrap: wrap;
}
.booking-card:hover { box-shadow: 0 4px 20px rgba(14,31,23,.08); }

.booking-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.booking-info { flex: 1; min-width: 160px; }
.booking-route { font-weight: 700; font-size: 14px; }
.booking-meta { font-size: 12px; color: ${T.smoke}; margin-top: 3px; }
.booking-status-pill { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 50px; letter-spacing: .05em; }
.booking-ref { font-family: 'DM Mono', monospace; font-size: 11px; background: ${T.creamDark}; padding: 4px 10px; border-radius: 6px; color: ${T.forest}; font-weight: 500; flex-shrink: 0; }
.booking-amount { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: ${T.forest}; text-align: right; flex-shrink: 0; }
.dl-btn {
  font-size: 11px; font-weight: 700; padding: 4px 10px;
  background: ${T.forest}; color: ${T.white};
  border: none; border-radius: 6px; cursor: pointer;
  font-family: 'DM Sans', sans-serif; white-space: nowrap;
}
.dl-btn:hover { background: ${T.gold}; color: ${T.forest}; }

.empty-state { text-align: center; padding: 48px 24px; background: ${T.white}; border-radius: 16px; border: 1.5px dashed ${T.creamDark}; }
.empty-icon { font-size: 44px; margin-bottom: 12px; }
.empty-text { color: ${T.smoke}; font-size: 15px; }

.results-meta { font-size: 13px; color: ${T.smoke}; margin-bottom: 14px; }


.drawer-overlay {
  position: fixed; inset: 0; background: rgba(14,31,23,.45);
  z-index: 1000;
}
.drawer {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: 100%; max-width: 460px;
  background: ${T.white}; z-index: 1001;
  overflow-y: auto; display: flex; flex-direction: column;
  border-left: 1px solid ${T.creamDark};
}
.drawer-head {
  background: ${T.forest}; color: ${T.white};
  padding: 18px 20px; display: flex; align-items: center; gap: 12px;
  flex-shrink: 0;
}
.drawer-close {
  background: rgba(255,255,255,.15); border: none; color: ${T.white};
  width: 32px; height: 32px; border-radius: 8px; cursor: pointer;
  font-size: 18px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.drawer-close:hover { background: rgba(255,255,255,.25); }
.drawer-title { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; flex: 1; }

.drawer-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; flex: 1; }

.trip-summary {
  background: ${T.cream}; border-radius: 12px; padding: 14px 16px;
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
}
.trip-summary-route { font-weight: 700; font-size: 15px; color: ${T.forest}; }
.trip-summary-meta  { font-size: 12px; color: ${T.smoke}; margin-top: 3px; }
.trip-summary-price { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: ${T.forest}; white-space: nowrap; }

.form-label { font-size: 11px; font-weight: 700; color: ${T.smoke}; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 8px; display: block; }

.seat-picker-wrap {
  background: ${T.cream};
  border-radius: 14px;
  padding: 16px;
  border: 1.5px solid ${T.creamDark};
}
.seat-picker-legend { display: flex; gap: 16px; margin-bottom: 14px; flex-wrap: wrap; }
.seat-legend-item { display: flex; align-items: center; gap: 6px; font-size: 11px; color: ${T.smoke}; font-weight: 500; }
.seat-legend-dot { width: 16px; height: 22px; border-radius: 5px 5px 3px 3px; flex-shrink: 0; }
.seat-legend-dot.available { background: ${T.white}; border: 1.5px solid ${T.creamDark}; }
.seat-legend-dot.taken     { background: #e0ddd8; border: 1.5px solid #ccc; }
.seat-legend-dot.selected  { background: ${T.gold}; border: 1.5px solid #a07320; }
.seat-car { background: ${T.white}; border-radius: 16px; border: 2px solid ${T.creamDark}; padding: 14px 10px; display: flex; flex-direction: column; gap: 0; position: relative; }
.seat-car-head { text-align: center; font-size: 11px; font-weight: 700; color: ${T.smoke}; letter-spacing: .12em; text-transform: uppercase; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1.5px dashed ${T.creamDark}; }
.seat-col-header { display: flex; align-items: center; padding: 0 4px 6px; }
.seat-col-label { width: 42px; text-align: center; font-size: 10px; font-weight: 700; color: ${T.smoke}; text-transform: uppercase; letter-spacing: .1em; flex-shrink: 0; }
.seat-col-spacer { flex: 1; }
.seat-aisle-label { width: 32px; text-align: center; font-size: 9px; color: ${T.smoke}; letter-spacing: .08em; }
.seat-row { display: flex; align-items: center; gap: 4px; padding: 3px 4px; border-radius: 6px; transition: background .15s; }
.seat-row:hover { background: rgba(26,61,43,.03); }
.seat-row-num { width: 20px; text-align: center; font-size: 10px; font-family: 'DM Mono', monospace; color: ${T.smoke}; flex-shrink: 0; }
.seat-btn { width: 42px; height: 52px; border-radius: 7px 7px 4px 4px; border: 1.5px solid ${T.creamDark}; background: ${T.white}; cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 600; color: ${T.forest}; transition: all .15s; position: relative; flex-shrink: 0; }
.seat-btn::before { content: ''; position: absolute; top: -5px; left: 6px; right: 6px; height: 5px; background: inherit; border: 1.5px solid ${T.creamDark}; border-bottom: none; border-radius: 4px 4px 0 0; }
.seat-btn:hover:not(.seat-taken):not(.seat-selected) { border-color: ${T.forestMid}; background: rgba(26,61,43,.06); transform: translateY(-1px); }
.seat-btn.seat-selected { background: ${T.gold}; border-color: #a07320; color: ${T.forest}; box-shadow: 0 3px 10px rgba(200,148,42,.4); transform: translateY(-2px); }
.seat-btn.seat-selected::before { background: ${T.gold}; border-color: #a07320; }
.seat-btn.seat-taken { background: #e0ddd8; border-color: #c8c5bf; color: #b0ada8; cursor: not-allowed; }
.seat-btn.seat-taken::before { background: #e0ddd8; border-color: #c8c5bf; }
.seat-btn-num { font-size: 11px; font-weight: 700; line-height: 1; }
.seat-btn-icon { font-size: 9px; line-height: 1; }
.seat-aisle { width: 32px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.seat-aisle-line { width: 1px; height: 40px; background: linear-gradient(to bottom, transparent, ${T.creamDark}, transparent); }
.seat-no-pref { margin-top: 10px; padding-top: 10px; border-top: 1.5px dashed ${T.creamDark}; display: flex; align-items: center; justify-content: center; }
.seat-no-pref-btn { font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; color: ${T.smoke}; background: none; border: 1.5px solid ${T.creamDark}; border-radius: 8px; padding: 7px 16px; cursor: pointer; transition: all .15s; }
.seat-no-pref-btn:hover { border-color: ${T.forest}; color: ${T.forest}; background: rgba(26,61,43,.04); }
.seat-no-pref-btn.active { background: ${T.forest}; color: ${T.white}; border-color: ${T.forest}; }
.seat-selection-info { margin-top: 8px; padding: 8px 12px; background: rgba(200,148,42,.1); border-radius: 8px; font-size: 12px; color: #a07320; font-weight: 600; display: flex; align-items: center; gap: 6px; }

.pay-options { display: flex; flex-direction: column; gap: 8px; }
.pay-option { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 10px; border: 1.5px solid ${T.creamDark}; background: ${T.cream}; cursor: pointer; transition: border-color .15s, background .15s; }
.pay-option:hover { border-color: ${T.forestMid}; }
.pay-option.selected { border-color: ${T.forest}; background: rgba(26,61,43,.06); }
.pay-option-icon { font-size: 22px; flex-shrink: 0; }
.pay-option-name { font-weight: 600; font-size: 14px; color: ${T.ink}; }

.drawer-input { width: 100%; padding: 11px 14px; border: 1.5px solid ${T.creamDark}; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 14px; background: ${T.cream}; outline: none; }
.drawer-input:focus { border-color: ${T.forest}; background: ${T.white}; }

.drawer-btn { width: 100%; padding: 14px; background: ${T.forest}; color: ${T.white}; border: none; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700; cursor: pointer; }
.drawer-btn:disabled { opacity: .55; cursor: not-allowed; }
.drawer-btn:not(:disabled):hover { background: #142e20; }

.ref-box { background: ${T.creamDark}; border-radius: 10px; padding: 12px 14px; font-family: 'DM Mono', monospace; font-size: 13px; color: ${T.forest}; font-weight: 600; }

.done-box { text-align: center; padding: 16px 0; display: flex; flex-direction: column; gap: 12px; }
.done-box .done-icon { font-size: 48px; }
.done-box .done-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: ${T.forest}; }
.done-box .done-ref   { font-size: 13px; color: ${T.smoke}; }

.pay-instructions { background: ${T.cream}; border-radius: 12px; padding: 14px 16px; border-left: 3px solid ${T.gold}; }
.pay-instructions-title { font-weight: 700; font-size: 13px; color: ${T.forest}; margin-bottom: 10px; }
.pay-steps { padding-left: 0; list-style: none; display: flex; flex-direction: column; gap: 8px; }
.pay-step { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: ${T.ink}; }
.pay-step-num { width: 22px; height: 22px; border-radius: 50%; background: ${T.forest}; color: ${T.white}; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pay-tip { margin-top: 10px; font-size: 12px; color: ${T.smoke}; border-top: 1px solid ${T.creamDark}; padding-top: 8px; }

.ticket-preview { background: ${T.forest}; border-radius: 12px; padding: 16px; border-left: 5px solid ${T.gold}; text-align: left; display: flex; flex-direction: column; gap: 10px; }
.ticket-preview-head { display: flex; justify-content: space-between; align-items: center; font-size: 13px; }
.ticket-preview-route { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; color: ${T.white}; display: flex; align-items: center; gap: 8px; }
.ticket-arrow { color: ${T.gold}; }
.ticket-preview-details { display: flex; flex-wrap: wrap; gap: 8px; font-size: 12px; color: rgba(255,255,255,.7); border-top: 1px dashed rgba(255,255,255,.2); padding-top: 10px; }
.ticket-preview-details span { background: rgba(255,255,255,.1); padding: 3px 8px; border-radius: 50px; }
.ticket-preview-footer { display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: rgba(255,255,255,.8); border-top: 1px dashed rgba(255,255,255,.2); padding-top: 10px; }

.loading-screen { display: flex; align-items: center; justify-content: center; height: 70vh; font-size: 15px; color: ${T.smoke}; }
.spinner { width: 32px; height: 32px; border: 3px solid ${T.creamDark}; border-top-color: ${T.forest}; border-radius: 50%; animation: spin .8s linear infinite; margin-right: 12px; }

.nav-btn-ghost, .nav-btn-outline { display: inline-flex; align-items: center; gap: 6px; }
.nav-ico { display: none; line-height: 0; }

/* ── Mise en page de l'espace voyageur sur tablette / mobile ── */
.trip-mode { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.trip-mode button { padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif; }
.verify-banner { background: ${T.cream}; border: 1.5px solid ${T.creamDark}; border-radius: 12px; padding: 12px 16px; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.verify-banner button { background: ${T.forest}; color: #fff; border: none; border-radius: 8px; padding: 8px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif; }
.booking-side { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
.chat-fab { position: fixed; bottom: calc(24px + env(safe-area-inset-bottom)); right: 24px; z-index: 500; width: 56px; height: 56px; border-radius: 50%; background: ${T.forest}; color: #fff; border: none; box-shadow: 0 8px 24px rgba(0,0,0,.25); font-size: 24px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform .15s; }
.chat-fab:hover { transform: scale(1.05); }
.chat-panel { position: fixed; bottom: calc(92px + env(safe-area-inset-bottom)); right: 24px; z-index: 500; width: 340px; max-width: calc(100vw - 32px); background: #fff; border-radius: 18px; box-shadow: 0 16px 48px rgba(0,0,0,.25); overflow: hidden; display: flex; flex-direction: column; height: min(480px, calc(100vh - 140px)); animation: fadeUp .22s ease both; }
.chat-head { background: ${T.forest}; color: #fff; padding: 13px 16px; display: flex; align-items: flex-start; gap: 10px; }
.chat-close { display: none; margin-left: auto; background: rgba(255,255,255,.15); border: none; color: #fff; width: 32px; height: 32px; border-radius: 9px; cursor: pointer; font-size: 16px; }
.chat-body { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
.chat-input { display: flex; gap: 6px; padding: 10px; border-top: 1px solid ${T.creamDark}; }
.chat-input input { flex: 1; min-width: 0; border: 1.5px solid ${T.creamDark}; border-radius: 10px; padding: 10px 12px; font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; }
.chat-input input:focus { border-color: ${T.forest}; }
.chat-input button { background: ${T.forest}; color: #fff; border: none; border-radius: 10px; padding: 0 16px; font-weight: 700; cursor: pointer; }
.chat-input button:disabled { opacity: .5; cursor: not-allowed; }

@media (max-width: 700px) {
  .trip-card .trip-price-cell { display: flex; align-items: center; justify-content: space-between; gap: 12px; grid-column: 1 / -1; border-top: 1.5px dashed ${T.creamDark}; padding-top: 12px; }
  .trip-card .trip-price-cell .trip-currency { margin-bottom: 0; }
  .trip-card .trip-badges { grid-column: 1 / -1; }
}
@media (max-width: 640px) {
  .profile-card { gap: 14px; }
  .profile-avatar { width: 52px; height: 52px; font-size: 18px; }
  .profile-stats { margin-left: 0; width: 100%; justify-content: space-around; border-top: 1.5px dashed ${T.creamDark}; padding-top: 12px; }
  .stats-bar { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .stat-card { padding: 14px 12px; gap: 10px; }
  .stat-icon { width: 36px; height: 36px; font-size: 16px; }
  .stat-val { font-size: 20px; }
  .booking-card { align-items: flex-start; }
  .booking-dot { margin-top: 5px; }
  .booking-side { flex-direction: row; flex-wrap: wrap; align-items: center; justify-content: space-between; width: 100%; border-top: 1.5px dashed ${T.creamDark}; padding-top: 10px; }
  .booking-side .dl-btn { width: 100%; padding: 10px; font-size: 13px; border-radius: 9px; }
  .section-title { font-size: 18px; }
  .search-card { padding: 16px; }
}
@media (max-width: 560px) {
  .nav-label { display: none; }
  .nav-ico { display: inline-flex; }
  .nav-btn-ghost, .nav-btn-outline { padding: 8px 10px; }
  .nav-logo-sub { display: none; }
  .drawer { max-width: 100%; border-left: none; }
  .drawer-body { padding: 16px; padding-bottom: calc(20px + env(safe-area-inset-bottom)); }
  .trip-summary { flex-direction: column; gap: 6px; }
  .chat-fab { right: 16px; bottom: calc(16px + env(safe-area-inset-bottom)); width: 52px; height: 52px; }
  .chat-panel { inset: 0; width: auto; max-width: none; height: auto; border-radius: 0; bottom: 0; right: 0; z-index: 1100; padding-bottom: env(safe-area-inset-bottom); }
  .chat-close { display: flex; align-items: center; justify-content: center; }
  .chat-fab.open { display: none; }
}
@media (max-width: 380px) {
  .seat-btn { width: 38px; height: 48px; }
  .seat-col-label { width: 38px; }
  .seat-aisle, .seat-aisle-label { width: 22px; }
}

.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 10px; flex-wrap: wrap; }
.section-header .section-title { margin-bottom: 0; }
.section-badge { font-size: 12px; background: rgba(26,61,43,.1); color: ${T.forest}; padding: 4px 12px; border-radius: 50px; font-weight: 600; }

@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes spin { to { transform: rotate(360deg); } }
`;

/* ─────────────────────────── AUTOCOMPLETE INPUT ─────────────────────────── */
function AutocompleteInput({ value, onChange, placeholder, onEnter }) {
  const [open, setOpen]     = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef             = useRef(null);

  const suggestions = value.length >= 1
    ? STATIONS.filter((s) => s.toLowerCase().startsWith(value.toLowerCase()) && s.toLowerCase() !== value.toLowerCase())
    : [];

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (s) => { onChange(s); setOpen(false); setActive(-1); };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") onEnter?.();
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0) pick(suggestions[active]);
      else { setOpen(false); onEnter?.(); }
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="autocomplete-wrap" ref={wrapRef}>
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="autocomplete-list">
          {suggestions.map((s, i) => (
            <div
              key={s}
              className={`autocomplete-item${i === active ? " active" : ""}`}
              onMouseDown={() => pick(s)}
            >
              🚉 {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── SEAT PICKER ─────────────────────────── */
function SeatPicker({ value, onChange, trip }) {
  const totalSeats = trip?.totalSeats || 24;
  const occupied = new Set(trip?.occupiedSeats || trip?.takenSeats || []);
  const [blockedSeat, setBlockedSeat] = useState(null);

  const rows = Array.from({ length: Math.ceil(totalSeats / 4) }, (_, rowIdx) => {
    const base = rowIdx * 4;
    return [base + 1, base + 2, base + 3, base + 4].filter(n => n <= totalSeats);
  });

  const noPref = value === "";

  const clickSeat = (seatNum, taken, selected) => {
    if (taken) {
      setBlockedSeat(seatNum);
      setTimeout(() => setBlockedSeat(prev => prev === seatNum ? null : prev), 3000);
      return;
    }
    setBlockedSeat(null);
    onChange(selected ? "" : seatNum);
  };

  return (
    <div className="seat-picker-wrap">
      {blockedSeat && (
        <div role="alert" style={{ background:"#FEE2E2", border:"2px solid #EF4444", borderRadius:12, padding:"12px 16px", marginBottom:14, textAlign:"center" }}>
          <div style={{ fontWeight:800, fontSize:14, color:"#B91C1C" }}>Siège N°{blockedSeat} déjà occupé</div>
          <div style={{ fontSize:12, color:"#EF4444", marginTop:2 }}>Veuillez choisir un autre siège disponible.</div>
        </div>
      )}
      <div className="seat-picker-legend">
        <div className="seat-legend-item"><div className="seat-legend-dot available" />Disponible</div>
        <div className="seat-legend-item"><div className="seat-legend-dot taken" />Occupé</div>
        <div className="seat-legend-item"><div className="seat-legend-dot selected" />Sélectionné</div>
      </div>

      <div className="seat-car">
        <div className="seat-car-head">Wagon - Plan des sièges</div>
        <div className="seat-col-header">
          <div className="seat-row-num" />
          <div className="seat-col-label">A</div>
          <div className="seat-col-label">B</div>
          <div className="seat-aisle-label">│</div>
          <div className="seat-col-label">C</div>
          <div className="seat-col-label">D</div>
        </div>

        {rows.map((row, ri) => (
          <div className="seat-row" key={ri}>
            <div className="seat-row-num">{ri + 1}</div>
            {row.slice(0, 2).map((seatNum) => {
              const taken    = occupied.has(seatNum);
              const selected = String(value) === String(seatNum);
              return (
                <button
                  key={seatNum}
                  className={`seat-btn${taken ? " seat-taken" : ""}${selected ? " seat-selected" : ""}`}
                  onClick={() => clickSeat(seatNum, taken, selected)}
                  title={taken ? `Siège ${seatNum} - Occupé` : `Siège ${seatNum}`}
                >
                  <span className="seat-btn-num">{seatNum}</span>
                  <span className="seat-btn-icon">{taken ? "✕" : selected ? "✓" : ""}</span>
                </button>
              );
            })}
            <div className="seat-aisle"><div className="seat-aisle-line" /></div>
            {row.slice(2).map((seatNum) => {
              const taken    = occupied.has(seatNum);
              const selected = String(value) === String(seatNum);
              return (
                <button
                  key={seatNum}
                  className={`seat-btn${taken ? " seat-taken" : ""}${selected ? " seat-selected" : ""}`}
                  onClick={() => clickSeat(seatNum, taken, selected)}
                  title={taken ? `Siège ${seatNum} - Occupé` : `Siège ${seatNum}`}
                >
                  <span className="seat-btn-num">{seatNum}</span>
                  <span className="seat-btn-icon">{taken ? "✕" : selected ? "✓" : ""}</span>
                </button>
              );
            })}
          </div>
        ))}

        <div className="seat-no-pref">
          <button
            className={`seat-no-pref-btn${noPref ? " active" : ""}`}
            onClick={() => onChange("")}
          >
            {noPref ? "✓ Sans préférence de siège" : "Aucune préférence"}
          </button>
        </div>
      </div>

      {value !== "" && (
        <div className="seat-selection-info">
          💺 Siège {value} sélectionné -
          {(value % 4 === 1 || value % 4 === 0) ? " côté fenêtre" : " côté couloir"}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────── OPERATORS ─────────────────────────── */
const OPERATORS = [
  {
    id: "AIRTEL_MONEY", icon: "📱", name: "Airtel Money",
    steps: [
      "Composez le *555# sur votre téléphone Airtel",
      "Choisissez « Paiement marchand »",
      "Entrez le code marchand : SETRAG",
      "Saisissez le montant exact indiqué",
      "Entrez votre code PIN Airtel Money",
      "Notez la référence de transaction reçue par SMS",
    ],
  },
  {
    id: "MOOV_MONEY", icon: "💙", name: "Moov Money",
    steps: [
      "Composez le *155# sur votre téléphone Moov",
      "Choisissez « Payer une facture »",
      "Entrez le code biller : SETRAG",
      "Saisissez le montant exact indiqué",
      "Entrez votre code PIN Moov Money",
      "Notez la référence de transaction reçue par SMS",
    ],
  },
];

/* ── Canvas ticket download ── */
// Construit un objet ticket exploitable par downloadTicketsPdf à partir
// d'une ligne de /my-bookings ou de la réponse de verify-payment - les deux
// formes différent légèrement, donc on prend le champ disponible en premier.
function ticketFromDashboardBooking(b, fallbackUser) {
  const trip = b.trip || {};
  return {
    reference: b.reference,
    passenger: b.passenger || b.passengerName || b.fullName || fallbackUser?.name || "",
    seat: b.seat ?? b.seatNumber,
    depart: b.departure || trip.departure,
    arrivee: b.arrival || trip.arrival,
    date: b.date || trip.date,
    heure: b.departureTime || trip.departureTime,
    train: b.trainNumber || trip.trainNumber,
    classe: b.travelClass || trip.travelClass,
    prix: b.amount ?? trip.price,
    tripType: b.tripType || "ALLER",
    qrPayload: b.qrPayload || null,
  };
}

/* ─────────────────────────── TRIP ROW (aller & retour) ─────────────────────────── */
function TripRow({ t, i, onClick }) {
  const cls   = CLASS_MAP[t.travelClass] || CLASS_MAP.ECONOMIQUE;
  const isLow = (t.seats ?? t.availableSeats) <= 5;
  return (
    <div className="trip-card" style={{ animationDelay: `${i * 0.06}s` }} onClick={onClick}>
      <div className="tc-dep">
        <div className="station-time">{t.departureTime}</div>
        <div className="station-name">{t.departure?.name || t.departure}</div>
        <div className="station-code">{t.departure?.stationCode || t.departure?.code || ""}</div>
      </div>
      <div className="trip-middle">
        <div className="trip-line">
          <div className="trip-dot" />
          <div className="trip-dash" />
          <div className="trip-dot" />
        </div>
        <div className="trip-duration">⏱ {t.duration || "-"}</div>
      </div>
      <div className="tc-arr">
        <div className="station-time">{t.arrivalTime}</div>
        <div className="station-name">{t.arrival?.name || t.arrival}</div>
        <div className="station-code">{t.arrival?.stationCode || t.arrival?.code || ""}</div>
      </div>
      <div className="trip-badges">
        <span className="badge badge-class">{cls.icon} {cls.label}</span>
        <span className={`badge badge-seats${isLow ? " low" : ""}`}>
          {isLow ? "⚠️" : "✅"} {t.seats ?? t.availableSeats ?? "?"} places
        </span>
        <span className="badge badge-train">🚂 {t.trainNumber}</span>
      </div>
      <div className="trip-price-cell">
        <div>
          <div className="trip-price">{Number(t.price).toLocaleString("fr-FR")}</div>
          <div className="trip-currency">FCFA / pers.</div>
        </div>
        <button className="book-btn" onClick={(e) => { e.stopPropagation(); onClick(); }}>
          Réserver →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── BOOKING DRAWER ─────────────────────────── */
function BookingDrawer({ trip, user, leg, isRoundTrip, returnDate, onClose, onConfirmed, showToast }) {
  const [seat, setSeat]               = useState("");
  const [operator, setOperator]       = useState(null);
  const [phone, setPhone]             = useState(user?.phone || "");
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading]         = useState(false);

  const loadingRef     = useRef(false);
  const bookingDataRef = useRef(null);

  const cls        = CLASS_MAP[trip.travelClass] || CLASS_MAP.ECONOMIQUE;
  const selectedOp = OPERATORS.find((o) => o.id === operator);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const handleBook = async () => {
    if (!operator) { showToast("Choisissez un mode de paiement", "warning"); return; }
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await api.post("/bookings", {
        tripId:      trip.id,
        fullName:    user?.name || "Voyageur",
        seatNumber:  seat ? Number(seat) : null,
        operator,
        tripType:    leg || "ALLER",
        isRoundTrip: !!isRoundTrip,
        returnDate:  isRoundTrip ? returnDate : null,
      });
      const raw = res?.data?.data ?? res?.data ?? {};
      const bookingId = raw.bookingId ?? raw.booking_id ?? raw.id ?? raw.booking?.id ?? null;
      const newData = {
        bookingId,
        reference: raw.reference ?? raw.ref ?? "REF-" + Date.now(),
      };
      bookingDataRef.current = newData;
      setBookingData(newData);
    } catch (e) {
      showToast(errorMessage(e, "La réservation n'a pas pu être créée."), "error");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handlePay = async () => {
    if (!phone.trim()) { showToast("Entrez le numéro de téléphone utilisé pour le paiement.", "warning"); return; }
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    const currentBooking = bookingDataRef.current;
    if (!currentBooking?.bookingId) {
      showToast("Identifiant de réservation introuvable - relancez la réservation", "error");
      setLoading(false);
      loadingRef.current = false;
      return;
    }

    try {
      const verifyRes = await api.post("/verify-payment", {
        bookingId:            currentBooking.bookingId,
        transactionReference: "TXN-" + Math.random().toString(36).substring(2, 10).toUpperCase(),
        operator,
        phoneNumber:          phone.trim(),
      });
      const paid = { ...currentBooking, paid: true, qrPayload: verifyRes.data?.booking?.qrPayload || null };
      bookingDataRef.current = paid;
      setBookingData(paid);
      onConfirmed(leg || "ALLER");
      showToast("Réservation confirmée. Bon voyage !", "success");
    } catch (e) {
      showToast(errorMessage(e, "Le paiement n'a pas pu être confirmé."), "error");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (!bookingDataRef.current?.reference) {
      showToast("Billet introuvable - impossible de générer le PDF", "error");
      return;
    }
    setDownloading(true);
    try {
      const bd = bookingDataRef.current || {};
      await downloadTicketsPdf([{
        reference: bd.reference,
        passenger: user?.name || "",
        seat: seat,
        depart: trip.departure?.name || trip.departure,
        arrivee: trip.arrival?.name || trip.arrival,
        date: trip.date,
        heure: trip.departureTime,
        train: trip.trainNumber,
        classe: trip.travelClass,
        prix: trip.price,
        tripType: leg || "ALLER",
        qrPayload: bd.qrPayload || null,
      }], `billet-setrag-${bd.reference || Date.now()}.pdf`);
      showToast("Votre billet PDF a été téléchargé.", "success");
    } catch (e) {
      showToast("Échec du téléchargement : " + errorMessage(e, "erreur inconnue"), "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-head">
          <button className="drawer-close" onClick={onClose} aria-label="Fermer">←</button>
          <div className="drawer-title">
            {bookingData?.paid ? "✅ Billet confirmé" : bookingData ? "💳 Paiement" : "🎫 Réserver"}
            {isRoundTrip && <span style={{ fontWeight: 600, fontSize: 12, opacity: 0.7, marginLeft: 6 }}>
              - {leg === "RETOUR" ? "trajet retour" : "trajet aller"}
            </span>}
          </div>
        </div>

        <div className="drawer-body">
          <div className="trip-summary">
            <div>
              <div className="trip-summary-route">
                {trip.departure?.name || trip.departure} → {trip.arrival?.name || trip.arrival}
              </div>
              <div className="trip-summary-meta">
                {trip.departureTime} – {trip.arrivalTime} · {cls.icon} {cls.label} · 🚂 {trip.trainNumber}
              </div>
            </div>
            <div className="trip-summary-price">{fmtPrice(trip.price)}</div>
          </div>

          {/* ÉTAPE 1 */}
          {!bookingData && (
            <>
              <div>
                <label className="form-label">Choisissez votre siège</label>
                <SeatPicker value={seat} onChange={setSeat} trip={trip} />
              </div>
              <div>
                <label className="form-label">Mode de paiement</label>
                <div className="pay-options">
                  {OPERATORS.map((op) => (
                    <div
                      key={op.id}
                      className={`pay-option${operator === op.id ? " selected" : ""}`}
                      onClick={() => setOperator(op.id)}
                    >
                      <span className="pay-option-icon">{op.icon}</span>
                      <span className="pay-option-name">{op.name}</span>
                      {operator === op.id && (
                        <span style={{ marginLeft: "auto", color: T.forest, fontWeight: 700 }}>✓</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {selectedOp && (
                <div className="pay-instructions">
                  <div className="pay-instructions-title">{selectedOp.icon} Comment payer avec {selectedOp.name}</div>
                  <ol className="pay-steps">
                    {selectedOp.steps.map((s, i) => (
                      <li key={i} className="pay-step">
                        <span className="pay-step-num">{i + 1}</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="pay-tip">💡 Gardez votre téléphone à portée - vous recevrez un SMS de confirmation.</div>
                </div>
              )}
              <button className="drawer-btn" onClick={handleBook} disabled={loading || !operator}>
                {loading ? "⏳ Création…" : "Continuer →"}
              </button>
            </>
          )}

          {/* ÉTAPE 2 */}
          {bookingData && !bookingData.paid && (
            <>
              <div className="ref-box">
                <span style={{ fontSize: 11, color: T.smoke, display: "block", marginBottom: 4 }}>RÉFÉRENCE</span>
                {bookingData.reference}
              </div>
              <div className="pay-instructions">
                <div className="pay-instructions-title">{selectedOp?.icon} Finaliser le paiement {selectedOp?.name}</div>
                <ol className="pay-steps">
                  {selectedOp?.steps.map((s, i) => (
                    <li key={i} className="pay-step">
                      <span className="pay-step-num">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
                <div className="pay-tip">Montant exact : <strong style={{ color: T.forest }}>{fmtPrice(trip.price)}</strong></div>
              </div>
              <div>
                <label className="form-label">Votre numéro {selectedOp?.name}</label>
                <input
                  className="drawer-input"
                  type="tel"
                  placeholder="Ex : 074 000 000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <button className="drawer-btn" onClick={handlePay} disabled={loading}>
                {loading ? "⏳ Vérification…" : "✅ Confirmer le paiement"}
              </button>
            </>
          )}

          {/* ÉTAPE 3 */}
          {bookingData?.paid && (
            <div className="done-box">
              <div className="done-icon">✅</div>
              <div className="done-title">Bon voyage !</div>
              <div className="done-ref">
                Réf : <strong style={{ fontFamily: "monospace", color: T.forest }}>{bookingData.reference}</strong>
              </div>
              <div className="ticket-preview">
                <div className="ticket-preview-head">
                  <span style={{ fontWeight: 800, color: T.gold }}>🚆 SETRAG</span>
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: T.smoke }}>{bookingData.reference}</span>
                </div>
                <div className="ticket-preview-route">
                  {trip.departure?.name || trip.departure}
                  <span className="ticket-arrow">→</span>
                  {trip.arrival?.name || trip.arrival}
                </div>
                <div className="ticket-preview-details">
                  <span>🕐 {trip.departureTime} – {trip.arrivalTime}</span>
                  <span>🚂 {trip.trainNumber}</span>
                  <span>{cls.icon} {cls.label}</span>
                  {seat && <span>💺 Siège {seat}</span>}
                </div>
                <div className="ticket-preview-footer">
                  <span>{user?.name}</span>
                  <span style={{ fontWeight: 700, color: T.gold }}>{fmtPrice(trip.price)}</span>
                </div>
                {bookingDataRef.current?.qrPayload && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
                    <TicketQR data={bookingDataRef.current.qrPayload} size={92} />
                  </div>
                )}
              </div>
              <button
                className="drawer-btn"
                style={{ background: T.gold, color: T.forest }}
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? "Génération du PDF…" : "⬇ Télécharger le billet (PDF)"}
              </button>
              <button
                className="drawer-btn"
                style={{ background: "transparent", color: T.forest, border: `1.5px solid ${T.creamDark}` }}
                onClick={onClose}
              >
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────── MAIN DASHBOARD ─────────────────────────── */
export default function Dashboard() {
  const navigate        = useNavigate();
  const { user, logout } = useAuth(); // ← utilise le contexte

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const todayStr = new Date().toISOString().split("T")[0];

  const [departure, setDeparture] = useState("");
  const [arrival, setArrival]     = useState("");
  const [date, setDate]           = useState(todayStr);
  const [trips, setTrips]         = useState([]);
  const [searched, setSearched]   = useState(false);
  const [searching, setSearching] = useState(false);

  // aller-retour
  const [tripMode, setTripMode]     = useState("ALLER");   // "ALLER" | "ALLER_RETOUR"
  const [returnDate, setReturnDate] = useState("");
  const [returnTrips, setReturnTrips] = useState([]);
  const [bookingLeg, setBookingLeg]   = useState("ALLER");
  const [legsBooked, setLegsBooked]   = useState({ ALLER: false, RETOUR: false });

  // aucun résultat : raison + dates alternatives
  const [noResultsReason, setNoResultsReason] = useState(null);
  const [altDates, setAltDates]               = useState([]);
  const [suggestLoading, setSuggestLoading]   = useState(false);

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [, startTransition]             = useTransition();
  const openModal  = (trip, leg = "ALLER") => startTransition(() => { setSelectedTrip(trip); setBookingLeg(leg); });
  const closeModal = ()     => startTransition(() => setSelectedTrip(null));

  const showToast = useShowToast();

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);

    const timeout = new Promise((_, rej) =>
      setTimeout(() => rej(new Error("timeout")), 8000)
    );

    Promise.race([
      api.get("/api/my-bookings").catch(() => ({ data: [] })),
      timeout,
    ])
      .then((bk) => {
        setBookings(Array.isArray(bk.data) ? bk.data : []);
      })
      .catch((err) => {
        if (err.message === "timeout") {
          showToast("La connexion est lente : vos billets peuvent mettre du temps à s'afficher. Rechargez la page si besoin.", "warning");
        } else {
          navigate("/login");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, showToast]);

  // Redirection si pas d'user (géré par le contexte)
  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchTrips = (dep, dest, d) =>
    api.get("/trips/search", { params: { departure: dep, destination: dest, date: d } });

  const findAlternativeDates = async (dep, dest, baseDate) => {
    setSuggestLoading(true);
    const offsets = [-1, 1, 2, 3, 4, 5, 7];
    const base = new Date(baseDate + "T00:00:00");
    const found = [];
    for (const off of offsets) {
      const d = new Date(base);
      d.setDate(d.getDate() + off);
      const dStr = d.toISOString().split("T")[0];
      if (dStr < todayStr) continue;
      try {
        const r = await fetchTrips(dep, dest, dStr);
        const count = Array.isArray(r.data) ? r.data.length : 0;
        if (count > 0) found.push({ date: dStr, count });
      } catch {}
      if (found.length >= 3) break;
    }
    setSuggestLoading(false);
    return found;
  };

  const handleSearch = async (overrideDate) => {
    const searchDate = overrideDate || date;
    if (overrideDate) setDate(overrideDate);

    if (!departure || !arrival) {
      showToast("Indiquez la gare de départ et la gare d'arrivée.", "warning");
      return;
    }
    if (!searchDate) { showToast("Veuillez choisir une date de voyage", "warning"); return; }
    if (searchDate < todayStr) {
      showToast("La date sélectionnée est déjà passée. Choisissez une date à partir d'aujourd'hui.", "warning");
      return;
    }
    if (tripMode === "ALLER_RETOUR") {
      if (!returnDate) { showToast("Veuillez choisir une date de retour", "warning"); return; }
      if (returnDate < searchDate) { showToast("La date de retour doit être égale ou postérieure à la date d'aller.", "warning"); return; }
    }

    setSearching(true);
    setNoResultsReason(null);
    setAltDates([]);
    try {
      const res = await fetchTrips(departure, arrival, searchDate);
      const outbound = Array.isArray(res.data) ? res.data : [];
      setTrips(outbound);

      let inbound = [];
      if (tripMode === "ALLER_RETOUR") {
        const resReturn = await fetchTrips(arrival, departure, returnDate);
        inbound = Array.isArray(resReturn.data) ? resReturn.data : [];
        setReturnTrips(inbound);
      } else {
        setReturnTrips([]);
      }

      setSearched(true);
      setLegsBooked({ ALLER: false, RETOUR: false });

      const noOutbound = outbound.length === 0;
      const noInbound  = tripMode === "ALLER_RETOUR" && inbound.length === 0;
      if (noOutbound || noInbound) {
        setNoResultsReason("NO_AVAILABILITY");
        const alts = await findAlternativeDates(
          noOutbound ? departure : arrival,
          noOutbound ? arrival   : departure,
          noOutbound ? searchDate : returnDate
        );
        setAltDates(alts);
      }
    } catch (e) {
      if (e.response?.status === 422 && e.response?.data?.reason === "DATE_IN_PAST") {
        setNoResultsReason("PAST_DATE");
        showToast(e.response.data.error, "warning");
      } else {
        showToast(errorMessage(e, "La recherche a échoué. Réessayez dans un instant."), "error");
      }
    } finally {
      setSearching(false);
    }
  };

  const applyAltDate = (d) => handleSearch(d);

  const refreshBookings = async () => {
    try {
      const bk = await api.get("/api/my-bookings");
      setBookings(Array.isArray(bk.data) ? bk.data : []);
    } catch {
      showToast("Votre liste de billets n'a pas pu être actualisée.", "warning");
    }
  };

  const initials = (name) =>
    (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="loading-screen">
          <div className="spinner" />
          Chargement de votre espace…
        </div>
      </>
    );
  }

  const confirmedCount = bookings.filter((b) => b.status === "CONFIRMED").length;

  return (
    <div className="setrag-dash">
      <style>{css}</style>

      {/* NAV */}
      <nav className="nav">
        <div className="nav-inner">
          <a href="/" className="nav-logo">
            <div className="nav-logo-icon">🚆</div>
            <div>
              <div className="nav-logo-title">SETRAG</div>
              <div className="nav-logo-sub">Mon espace</div>
            </div>
          </a>
          <div className="nav-actions">
            <button className="nav-btn-ghost" onClick={() => navigate("/")} title="Site principal" aria-label="Site principal">
              <span className="nav-ico"><HomeIcon size={17}/></span><span className="nav-label">← Site principal</span>
            </button>
            <div className="avatar" title={user?.name}>{initials(user?.name)}</div>
            <button className="nav-btn-outline" onClick={logout} title="Déconnexion" aria-label="Déconnexion">
              <span className="nav-ico"><LogoutIcon size={16}/></span><span className="nav-label">Déconnexion</span>
            </button>
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="dash-main">

        {/* Profile card */}
        <div className="profile-card">
          <div className="profile-avatar">{initials(user?.name)}</div>
          <div>
            <div className="profile-name">Bonjour, {user?.name} 👋</div>
            <div className="profile-meta">{user?.email}{user?.phone ? ` · ${user.phone}` : ""}</div>
          </div>
          <div className="profile-stats">
            <div className="profile-stat">
              <div className="profile-stat-val">{bookings.length}</div>
              <div className="profile-stat-label">Billets</div>
            </div>
            <div className="profile-stat">
              <div className="profile-stat-val">{confirmedCount}</div>
              <div className="profile-stat-label">Confirmés</div>
            </div>
          </div>
        </div>

        {/* Rappel de vérification (non bloquant) */}
        {(user?.isValidated === false || user?.isPhoneValidated === false) && (
          <div className="verify-banner" role="status">
            <span style={{ fontSize: 13, color: T.forest }}>
              🔐 Pensez à vérifier {user?.isValidated === false && user?.isPhoneValidated === false
                ? "votre e-mail et votre téléphone"
                : user?.isValidated === false ? "votre e-mail" : "votre téléphone"} pour sécuriser votre compte.
            </span>
            <button onClick={() => navigate("/verify-account", { state: { email: user?.email } })}>
              Vérifier maintenant
            </button>
          </div>
        )}

        {/* Stats bar */}
        <div className="stats-bar">
          {[
            { icon: "🚆", color: "green", val: "12",            label: "Trains actifs" },
            { icon: "🏙️", color: "gold",  val: "8",             label: "Gares desservies" },
            { icon: "🎫", color: "rust",  val: bookings.length, label: "Mes réservations" },
            { icon: "⭐", color: "green", val: "Or",            label: "Statut fidélité" },
          ].map((s, i) => (
            <div className="stat-card" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className={`stat-icon ${s.color}`}>{s.icon}</div>
              <div>
                <div className="stat-val">{s.val}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* SEARCH */}
        <div className="section" style={{ animationDelay: ".15s" }}>
          <div className="section-title">Rechercher un trajet</div>
          <div className="search-card">
            <div className="trip-mode" role="group" aria-label="Type de trajet">
              {[["ALLER", "Aller simple"], ["ALLER_RETOUR", "Aller-retour"]].map(([val, label]) => (
                <button key={val} type="button" aria-pressed={tripMode === val}
                  onClick={() => setTripMode(val)}
                  style={{
                    border: `1.5px solid ${tripMode === val ? T.forest : T.creamDark}`,
                    background: tripMode === val ? T.cream : "#fff",
                    color: tripMode === val ? T.forest : T.smoke,
                  }}>
                  {tripMode === val && "✓ "}{label}
                </button>
              ))}
            </div>
            <div className="search-grid">
              <div className="field">
                <label>Départ</label>
                <AutocompleteInput value={departure} onChange={setDeparture} placeholder="Ex : Libreville" onEnter={handleSearch} />
              </div>
              <div className="field">
                <label>Arrivée</label>
                <AutocompleteInput value={arrival} onChange={setArrival} placeholder="Ex : Franceville" onEnter={handleSearch} />
              </div>
              <div className="field">
                <label>Date</label>
                <input type="date" min={todayStr} value={date}
                  onChange={(e) => { setDate(e.target.value); if (returnDate && returnDate < e.target.value) setReturnDate(e.target.value); }} />
              </div>
              {tripMode === "ALLER_RETOUR" && (
                <div className="field">
                  <label>Date de retour</label>
                  <input type="date" min={date || todayStr} value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)} />
                </div>
              )}
              <div className="field search-btn-cell">
                <label style={{ opacity: 0, userSelect: "none" }} aria-hidden="true">.</label>
                <button className="search-btn" onClick={() => handleSearch()} disabled={searching}>
                  {searching ? "⏳ Recherche…" : "🔍 Rechercher"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SEARCH RESULTS */}
        {searched && (
          <div className="section" style={{ animationDelay: ".1s" }}>

            {noResultsReason && (
              <div className="empty-state">
                <div className="empty-icon">🚉</div>
                <p className="empty-text" style={{ fontWeight: 700, marginBottom: 4 }}>
                  {noResultsReason === "PAST_DATE" ? "Cette date est déjà passée" : "Aucune disponibilité pour ce trajet à cette date"}
                </p>
                <p className="empty-text" style={{ marginBottom: 14 }}>
                  {noResultsReason === "PAST_DATE"
                    ? "Choisissez une date à partir d'aujourd'hui."
                    : "Voici les prochaines dates disponibles sur ce trajet :"}
                </p>
                {suggestLoading && <p className="empty-text" style={{ fontSize: 12 }}>Recherche de dates alternatives…</p>}
                {!suggestLoading && altDates.length > 0 && (
                  <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                    {altDates.map(a => (
                      <button key={a.date} className="book-btn" onClick={() => applyAltDate(a.date)}>
                        {new Date(a.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}
                        {" "}· {a.count} dispo.
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {trips.length > 0 && (
              <>
                <div className="section-header">
                  <div className="section-title">{tripMode === "ALLER_RETOUR" ? "Trajet aller" : "Résultats"}</div>
                  <span className="section-badge">
                    {trips.length} trajet{trips.length !== 1 ? "s" : ""}
                    {legsBooked.ALLER && " · ✓ réservé"}
                  </span>
                </div>
                <div className="trips-list">
                  {trips.map((t, i) => (
                    <TripRow key={t.id} t={t} i={i} onClick={() => openModal(t, "ALLER")} />
                  ))}
                </div>
              </>
            )}

            {tripMode === "ALLER_RETOUR" && returnTrips.length > 0 && (
              <>
                <div className="section-header" style={{ marginTop: 24 }}>
                  <div className="section-title">Trajet retour</div>
                  <span className="section-badge">
                    {returnTrips.length} trajet{returnTrips.length !== 1 ? "s" : ""}
                    {legsBooked.RETOUR && " · ✓ réservé"}
                  </span>
                </div>
                <div className="trips-list">
                  {returnTrips.map((t, i) => (
                    <TripRow key={t.id} t={t} i={i} onClick={() => openModal(t, "RETOUR")} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* MES BILLETS */}
        <div className="section" style={{ animationDelay: ".2s" }}>
          <div className="section-header">
            <div className="section-title">Mes billets</div>
            <span className="section-badge">{bookings.length} billet{bookings.length !== 1 ? "s" : ""}</span>
          </div>
          {bookings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎟️</div>
              <p className="empty-text">Vous n'avez pas encore de réservation.<br />Recherchez un trajet ci-dessus.</p>
            </div>
          ) : (
            <div className="bookings-list">
              {bookings.map((b, i) => {
                const st  = STATUS[b.status] || STATUS.PENDING;
                const cls = CLASS_MAP[b.travelClass || b.trip?.travelClass] || CLASS_MAP.ECONOMIQUE;
                const route    = b.departure && b.arrival ? `${b.departure} → ${b.arrival}` : b.trip ? `${b.trip.departure} → ${b.trip.arrival}` : "-";
                const trainNum = b.trainNumber || b.trip?.trainNumber || "-";
                const tripDate = b.date || b.trip?.date || "-";
                const amount   = b.amount ?? b.trip?.price ?? 0;

                return (
                  <div className="booking-card" key={b.id} style={{ animationDelay: `${i * 0.07}s` }}>
                    <div className="booking-dot" style={{ background: st.dot }} />
                    <div className="booking-info">
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span className="booking-route">{route}</span>
                        <span className="booking-status-pill" style={{ background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </div>
                      <div className="booking-meta">
                        🚂 {trainNum} &nbsp;·&nbsp; {cls.icon} {cls.label}
                        {b.seat || b.seatNumber ? ` · Siège ${b.seat || b.seatNumber}` : ""}
                        &nbsp;·&nbsp; 📅 {tripDate}
                      </div>
                    </div>
                    <div className="booking-side">
                      <div className="booking-ref">{b.reference}</div>
                      <div className="booking-amount">{fmtPrice(amount)}</div>
                      {b.status === "CONFIRMED" && (
                        <button
                          className="dl-btn"
                          disabled={downloadingId === b.id}
                          onClick={async () => {
                            setDownloadingId(b.id);
                            try {
                              await downloadTicketsPdf(
                                [ticketFromDashboardBooking(b, user)],
                                `billet-setrag-${b.reference || b.id}.pdf`
                              );
                              showToast(`Billet ${b.reference} téléchargé.`, "success");
                            } catch (e) {
                              showToast("Échec du téléchargement : " + errorMessage(e, "erreur inconnue"), "error");
                            } finally {
                              setDownloadingId(null);
                            }
                          }}
                        >
                          {downloadingId === b.id ? "Génération…" : "⬇ Télécharger le billet"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* BOOKING MODAL */}
      {selectedTrip && (
        <BookingDrawer
          trip={selectedTrip}
          user={user}
          leg={bookingLeg}
          isRoundTrip={tripMode === "ALLER_RETOUR"}
          returnDate={returnDate}
          onClose={closeModal}
          onConfirmed={(confirmedLeg) => {
            refreshBookings();
            // On NE ferme PLUS la fenêtre ici : le paiement vient de réussir,
            // l'étape 3 (billet + QR code + téléchargement) doit rester
            // affichée. C'est l'utilisateur qui ferme lui-même une fois le
            // billet téléchargé (bouton "Fermer" dans BookingDrawer).
            setLegsBooked(prev => ({ ...prev, [confirmedLeg]: true }));
            if (tripMode === "ALLER_RETOUR" && confirmedLeg === "ALLER" && !legsBooked.RETOUR) {
              showToast("Trajet aller confirmé ✓ - sélectionnez maintenant votre trajet retour.", "success");
            }
          }}
          showToast={showToast}
        />
      )}

      {/* FOOTER */}
      <footer style={{
        background: T.forest, color: "rgba(255,255,255,.5)",
        textAlign: "center", padding: "20px", fontSize: 13, marginTop: 20,
        position: "relative", zIndex: 1,
      }}>
        <span style={{ color: T.goldLight, fontWeight: 700 }}>SETRAG</span>
        {" "}- Société d'Exploitation du Transgabonais &nbsp;|&nbsp; © 2025
      </footer>

      <SupportChatWidget />
    </div>
  );
}

/* ═══════════════════════════════════════
   SUPPORT - bouton flottant + fil de discussion
   Un voyageur connecté peut simplement voir le profil support et lui
   soumettre une réclamation, sans passer par le formulaire de contact
   public. Un seul fil par utilisateur (voir /api/my-conversation côté back).
═══════════════════════════════════════ */
function SupportChatWidget() {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get("/api/my-conversation"); setThread(r.data); }
    catch (e) { if (!silent) notify.error(errorMessage(e, "Impossible de joindre le support pour le moment.")); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { if (open && !thread) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actualisation automatique pendant que la fenêtre est ouverte - pas
  // besoin de recharger la page pour voir la réponse du support.
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => load(true), 5000);
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => { clearInterval(interval); document.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = thread?.messages?.length || 0;
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [count, open]);

  const send = async () => {
    if (!msg.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/api/my-conversation/reply", { body: msg.trim() });
      setMsg("");
      load(true);
    } catch (e) {
      notify.error(errorMessage(e, "Votre message n'a pas pu être envoyé. Réessayez."), { title: "Envoi impossible" });
    } finally { setSending(false); }
  };

  return (
    <>
      <button className={`chat-fab${open ? " open" : ""}`} onClick={() => setOpen(v => !v)}
        title="Contacter le support" aria-label={open ? "Fermer le support" : "Contacter le support"} aria-expanded={open}>
        {open ? "✕" : "💬"}
      </button>

      {open && (
        <div className="chat-panel" role="dialog" aria-label="Support SETRAG">
          <div className="chat-head">
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>💬 Support SETRAG</div>
              <div style={{ fontSize: 11.5, opacity: 0.8, marginTop: 2 }}>Une question, une réclamation ? Écrivez-nous.</div>
            </div>
            <button className="chat-close" onClick={() => setOpen(false)} aria-label="Fermer">✕</button>
          </div>
          <div className="chat-body" ref={bodyRef}>
            {loading && <p style={{ fontSize: 12.5, color: T.smoke }}>Chargement…</p>}
            {!loading && (!thread?.messages || thread.messages.length === 0) && (
              <p style={{ fontSize: 12.5, color: T.smoke }}>Aucun message pour l'instant : envoyez-nous votre question ci-dessous.</p>
            )}
            {thread?.messages?.map(m => (
              <div key={m.id} style={{ alignSelf: m.isMine ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <div style={{
                  background: m.isMine ? T.forest : T.cream, color: m.isMine ? "#fff" : T.forest,
                  borderRadius: 12, padding: "8px 12px", fontSize: 13.5, lineHeight: 1.45, whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                }}>
                  {m.body}
                </div>
              </div>
            ))}
          </div>
          <div className="chat-input">
            <input value={msg} onChange={e => setMsg(e.target.value)} placeholder="Votre message…" aria-label="Votre message"
              onKeyDown={e => e.key === "Enter" && send()} />
            <button onClick={send} disabled={sending || !msg.trim()} aria-label="Envoyer">➤</button>
          </div>
        </div>
      )}
    </>
  );
}
